import { slugify } from '../CommonJs/slug.js';
import { getUserAgent } from '../CommonJs/url.js';
import { pushDataLayerEvent } from '../CommonJs/dataLayer.js';

/** 
 * Search box element functionality. 
 * Handles searching and displaying the search results
 */
class Search {
    constructor(element) {
        this.userHasLocation = false;
        this.element = element;
        this.language = siteLanguage;
        this.input = this.element.querySelector(".js-input");
        this.placeholder = this.element.querySelector(".js-placeholder");
        this.dropdown = this.element.querySelector(".js-dropdown");
        this.dropdownResults = this.element.querySelector(".js-results");
        this.nonSearchItems = this.element.querySelector(".js-non-search-items");
        this.locationButton = this.element.querySelector(".js-user-location");
        this.locationButtonMessage = this.element.querySelector(".js-user-location-message");
        this.currentUserLocation = this.element.querySelector(".js-user-current-location");
        this.errorMessage = this.element.querySelector(".js-user-error-message");
        this.searchTimeout = null;
        this.flagsUrl = `${siteJSConfig?.AssetsPaths?.FlagsPath}{0}.png`
        this.crossIconUrl = `${siteJSConfig?.AssetsPaths?.IconsPath}cross.svg`;
        this.locationIconUrl = `${siteJSConfig?.AssetsPaths?.IconsPath}location.svg`;
        this.gpsIconUrl = `${siteJSConfig?.AssetsPaths?.IconsPath}gps.svg`;
        this.emptySearchButton = this.element.querySelector(".js-empty-search");
        this.emptySearchButton?.addEventListener("click", () => this.emptySearch())

        this.locationButton.addEventListener("click", (e) => this.getUserLocation(e));
        this.input.addEventListener("input", (e) => this.onInput(e));
        this.input.addEventListener("blur", this.collapse);
        this.input.addEventListener("focus", this.expand);
        this.dropdown.addEventListener("mousedown", (e) => e.preventDefault()); // Clicks inside dropdown won't dismiss the popup
    }

    static init() {
        const components = document.querySelectorAll(`*[data-component="search"]`);
        components.forEach(element => Search.elements.push(new Search(element)));
    }

    /** Static method to collapse all search dropdowns. Can be used e.g. in the menu. */
    static collapseAll() {
        Search.elements.forEach(search => search.collapse());
    }

    /** Returns url to correct page */
    static getRelativeUrl(location) {
        if (location.type === "location") {
            return Search.getRelativeCityUrl(location);
        }
    }

    /** Returns url to correct page, prepended to current langage (e.g. /en-gb) in url */
    static getRelativeCityUrl(location) {
        let cityUrl = `/${slugify(location.country_name)}/${slugify(location.name)}/${location.location_id}`;

        return cityUrl;
    }

    onInput(e) {
        // Force file hash refresh
        const query = encodeURIComponent(this.input.value);

        if (query.length == 0) {
            // Query is empty
            this.emptySearchButton.classList.add("hidden");
            this.dropdownResults.innerHTML = "";
        } else {
            // User has typed
            this.emptySearchButton.classList.remove("hidden");
        }

        this.updateSearch(query);
        //query.length >= 3 && this.emptyResults();
    }

    /** Expand search dropdown */
    expand = () => {
        this.dropdown.style.display = "block";

        if (this.placeholder) {
            this.placeholder.classList.add("hidden");
        }
    }

    /** collapse search dropdown */
    collapse = () => {
        this.dropdown.style.display = "none";
        if (this.placeholder) {
            this.placeholder.classList.remove("hidden");
        }

        this.resetLocation();
    }

    /** Query changed */
    updateSearch(query) {
        if (this.input.value.length < 3) return;
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => this.search(query), 200);
    }

    locationFilter(loc) {
        return loc.type == "location";
    }

    searchResultsFlag(location) {
        if (location.country_code) return this.flagsUrl.replace('{0}', location.country_code?.toLowerCase());

        return this.regionIconUrl;
    }

    searchResultsText(location) {
        return `${location.name} <span class="text-xs text-text-secondary ml-2">${location?.admin_name}, ${location?.country_name}</span>`;
    }

    /** Search on given query */
    async search(query, limit = 10) {
        const url = `/search?lang=${this.language}&query=${query}&limit=${limit}`;

        try {
            const response = await fetch(url, {
                headers: {
                    'User-Agent': getUserAgent()
                }
            })
                .then(response => response.json())
                .then(data => data.filter(this.locationFilter));

            if (response.length == 0) {
                this.emptyResults();
            }

            const results = response.map((location) => ({
                icon: this.searchResultsFlag(location),
                href: Search.getRelativeUrl(location),
                text: this.searchResultsText(location),
                location: location,
            }))

            this.setSearchResults(results);
        } catch (e) {
            console.error(e);
            this.setErrorState();
        }
    }

    setErrorState() {
        this.dropdownResults.innerHTML = `
            <p class="px-2 py-1">
                <span class="icon-times-circle align-[-1px] mr-1 text-red"></span> 
                Zoeken is niet beschikbaar op dit moment.
            </p>`;
    }

    setSearchResults(results) {
        this.dropdownResults.innerHTML = "";

        if (results.length == 0) {
            const noResultsText = this.dropdownResults.getAttribute("data-no-results");
            this.dropdownResults.innerHTML = `<p class="py-3">${noResultsText}</p>`;
        } else {
            for (let result of results) {
                const resultElement = document.createElement("li");

                resultElement.innerHTML = `
                    <li class="border-b border-[#ccc] last:border-b-0">
                        <a class="flex items-center hover:text-main w-full py-3" href="${result.href}">
                            <img width="16" height="11" class="inline-block mx-2 pointer-events-none align-[1px]" src="${result.icon}" />
                            <span>${result.text}</span>
                        </a>
                    </li>`;

                this.dropdownResults.appendChild(resultElement);

                resultElement.addEventListener("click", () => {
                    pushDataLayerEvent({
                        event: 'select_search_result',
                        click_name: `${result.location.name}, ${result.location.country_name}`,
                        ...(result.location.location_id && { location_id: String(result.location.location_id) })
                    });
                }, { once: true });
            }
        }
    }

    /** Reset search results to blank */
    emptyResults() { 
        this.setSearchResults([]);
    }

    /** Empty search box */
    emptySearch() {
        this.input.value = "";
        this.emptySearchButton.classList.add("hidden");
        this.dropdownResults.innerHTML = "";
    }

    /** Get user gps coordinates and fetch location */
    getUserLocation(e) {
        e.preventDefault();

        this.setLoadingLocation();
        const options = {
            enableHighAccuracy: false,
            timeout: 5000,
            maximumAge: 10000,
        };
        navigator.geolocation?.getCurrentPosition(
            (position) => this.onLocationSucess(position),
            (err) => this.onLocationError(err),
            options
        );
    }

    /** Run when the browser finds the user location */
    onLocationSucess(position) {
        const lat = Math.round(position?.coords?.latitude * 100) / 100;
        const lon = Math.round(position?.coords?.longitude * 100) / 100;
        this.nearbySearch(lat, lon);
    }

    /** search on given coordinates and return closest location */
    async nearbySearch(lat, lon, radius = 3000, count = 1) {
        const url = `/search/nearby?lang=${this.language}&Lat=${lat}&Lon=${lon}&Radius=${radius}&count=${count}`;

        try {
            const response = await fetch(url, {
                headers: {
                    'User-Agent': getUserAgent()
                }
            }).then(response => response.json());
            const location = response && response.length && response[0];

            // Insert Result
            if (!location) {
                this.onLocationError({ code: 404, message: "location not found" });
                return;
            }

            // check for correct API response
            if (!location.location_id) return this.onLocationError({ code: 404, message: "location error" });

            pushDataLayerEvent({
                event: 'select_nearby_search_result',
                click_name: `${location.name}, ${location.country_name}`,
                ...(location.location_id && { location_id: String(location.location_id) })
            });

            // navigate to location url
            window.location.href = Search.getRelativeUrl(location);
        } catch (e) {
            this.onLocationError(e);
        }
    }

    /** When getting location fails */
    onLocationError(err) {
        console.error(err);
        this.locationButton.classList.add("hidden");
        this.locationButtonMessage.classList.add("hidden");
        this.errorMessage.classList.remove("hidden");
    }

    /** set loading location */
    setLoadingLocation() {
        this.locationButton.classList.add("hidden");
        this.errorMessage.classList.add("hidden");
        this.locationButtonMessage.classList.remove("hidden");
    }

    /** reset get location button */
    resetLocation() {
        if (this.userHasLocation) {
            this.currentUserLocation.classList.remove("hidden");
        } else {
            this.locationButton.classList.remove("hidden");
        }

        this.locationButtonMessage.classList.add("hidden");
        this.errorMessage.classList.add("hidden");
    }
}

/** Global Search element tally */
Search.elements = [];

document.addEventListener('DOMContentLoaded', () => {
    Search.init();
});
